import { useNavigate, useLocation } from "react-router-dom";
import { PALETTE } from "../tokens";

// Shared bottom navigation — single source of truth for the five-tab bar.
// Replaces the per-screen inline copies that had drifted out of sync.
//
// Layout:  Home · Protocol · [Signal] · Journal · Profile
// Signal is a raised gold circular button (center-FAB / Instagram-camera
// pattern) using the existing waveform Signal symbol. The other four are
// standard flat icon + label tabs.
//
// Journal replaced Domains in this slot. Domains was a tab pointing at
// one arbitrary domain (/domain/bio) with the other two reachable only
// from inside it — a browsing surface, not a daily one. The per-domain
// detail screens are unchanged and still live at /domain/:code; their
// entry point moved to the Profile summary card (see Profile.jsx).
//
// Active treatment:
//   • On a flat tab  → that tab's icon + label go gold (PALETTE.primary).
//   • On Signal      → the gold button deepens and gains a ring; the four
//                      flat tabs stay muted.
//   • Off Signal     → the Signal button stays gold-but-passive.

const ICON = {
  home:
    "M3 12L12 3l9 9M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9",
  protocol:
    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  signal:
    "M22 12h-4l-3 9L9 3l-3 9H2",
  // Open book — two facing pages on a spine. Same 24×24 viewBox and
  // 1.6 stroke as its neighbours, line-art only, no fill.
  journal:
    "M12 7v13M12 7a4 4 0 00-4-4H3v13h5a4 4 0 014 4M12 7a4 4 0 014-4h5v13h-5a4 4 0 00-4 4",
  profile:
    "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z",
};

// `href` is the navigation target; `match` is the path prefix (or list
// of prefixes) that marks the tab active. Protocol also matches
// /evening: the evening protocol's surface is the guided ritual and the
// tab routes there at night — the ritual's launch screen renders this
// nav, so the tab should read as "you are here".
//
// Profile also matches /domain: the per-domain detail screens are now
// reached from Profile's domain summary card, so Profile is their
// parent surface. Without this, a user on /domain/bio would see NO tab
// highlighted — the honest alternative to highlighting a tab that no
// longer exists, but a worse one, since the user did get there from
// somewhere and the bar should say where.
const LEFT = [
  { key: "home", label: "Home", href: "/dashboard", match: "/dashboard" },
  { key: "protocol", label: "Protocol", href: "/protocol", match: ["/protocol", "/evening"] },
];
const RIGHT = [
  { key: "journal", label: "Journal", href: "/journal", match: "/journal" },
  { key: "profile", label: "Profile", href: "/profile", match: ["/profile", "/domain"] },
];
const SIGNAL = { label: "Signal", href: "/signal", match: "/signal" };

const NAV_STYLES = `
  .bn-tab {
    flex: 1;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 8px 4px;
    transition: opacity 0.15s;
  }
  .bn-tab:hover { opacity: 0.65; }
  .bn-signal {
    flex: 1;
    position: relative;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 4px;
    min-height: 44px;
  }
  .bn-fab {
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 33px;
    height: 33px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: box-shadow 0.18s, transform 0.18s;
  }
  .bn-signal:hover .bn-fab { transform: translateX(-50%) translateY(-2px); }
`;

function FlatTab({ item, active, onClick }) {
  const color = active ? PALETTE.primary : PALETTE.textFaint;
  return (
    <button className="bn-tab" onClick={onClick} aria-label={item.label} aria-current={active ? "page" : undefined}>
      <svg
        width="23"
        height="23"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={ICON[item.key]} />
      </svg>
      <span
        style={{
          fontSize: 9,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontWeight: 500,
          color,
        }}
      >
        {item.label}
      </span>
    </button>
  );
}

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (match) =>
    [].concat(match).some((m) => location.pathname.startsWith(m));
  const signalActive = isActive(SIGNAL.match);

  // Passive gold sits proud of the bar with a bg-colored ring; active adds
  // a primary outline ring outside that gap so the button "gains a ring".
  const fabShadow = signalActive
    ? `0 0 0 4px ${PALETTE.bg}, 0 0 0 6px ${PALETTE.primary}, 0 3px 12px rgba(158,132,86,0.42)`
    : `0 0 0 4px ${PALETTE.bg}, 0 3px 10px rgba(158,132,86,0.30)`;

  return (
    <>
      <style>{NAV_STYLES}</style>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 420,
          zIndex: 10,
          background: `linear-gradient(to top, ${PALETTE.bg} 75%, transparent)`,
          paddingTop: 24,
          // Clear the home indicator on notched devices (viewport-fit=
          // cover makes the app paint under it); falls back to the old
          // flat 28px where there is no inset.
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 12px))",
        }}
      >
        <div
          style={{
            margin: "0 24px",
            background: PALETTE.surface,
            borderRadius: 20,
            padding: "10px 8px",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "flex-end",
            boxShadow: "0 4px 28px rgba(28,24,20,0.09)",
          }}
        >
          {LEFT.map((item) => (
            <FlatTab
              key={item.key}
              item={item}
              active={isActive(item.match)}
              onClick={() => navigate(item.href)}
            />
          ))}

          {/* Raised Signal center button */}
          <button
            className="bn-signal"
            onClick={() => navigate(SIGNAL.href)}
            aria-label="Signal"
            aria-current={signalActive ? "page" : undefined}
          >
            <span
              className="bn-fab"
              style={{
                background: PALETTE.primary,
                boxShadow: fabShadow,
                filter: signalActive ? "brightness(0.94)" : "none",
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke={PALETTE.bg}
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={ICON.signal} />
              </svg>
            </span>
            <span
              style={{
                marginTop: 28,
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 500,
                color: PALETTE.primary,
              }}
            >
              {SIGNAL.label}
            </span>
          </button>

          {RIGHT.map((item) => (
            <FlatTab
              key={item.key}
              item={item}
              active={isActive(item.match)}
              onClick={() => navigate(item.href)}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default BottomNav;
